/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
import { restartableTask } from 'ember-concurrency';
import { conformAmount } from 'linced-app/utils/finance';
import config from 'linced-app/config/environment';

export default class ReconciliationDialog extends Component {
  // Services
  @service
  fetch;

  @service
  roles;

  @service
  session;

  @service
  store;

  @service
  toaster;

  PaymentMethod = PaymentMethod;

  @tracked
  type = 'depositSlip';

  entity = null;
  loading = false;
  transactions = null;
  cancelled = null;
  discrepancies = null;

  @tracked
  showAllMethods = false;

  onClose() {}

  @computed('type', 'entity.isOpen')
  get title() {
    switch (this.type) {
      case 'depositSlip':
        return 'Deposit Slip';
      case 'tillSession':
        if (this.entity.isOpen) {
          return 'Close Till Session';
        }
        return 'Till Session';
    }
  }

  get summaryLabel() {
    switch (this.type) {
      case 'depositSlip':
        return 'Deposit';
      case 'tillSession':
        return 'Till Session';
    }
    return '';
  }

  get entityLabel() {
    switch (this.type) {
      case 'depositSlip':
        return 'Deposit Slip';
      case 'tillSession':
        return 'Till Session';
    }
    return 'record';
  }

  get totalLabel() {
    switch (this.type) {
      case 'depositSlip':
        return 'Banking';
    }
    return 'Balance';
  }

  amountInputOptions = Object.freeze({
    inputmode: 'decimal',
    pattern: '^\\d+(\\.\\d{1,2})?$',
    size: 8,
    autocomplete: 'off',
  });

  @tracked
  depositSlipChecked = false;

  @action
  setDepositSlipChecked(value) {
    this.depositSlipChecked = value;
  }

  @tracked
  discrepancy = null;

  @tracked
  discrepancyDialogOpen = false;

  @tracked
  discrepancySaving = false;

  @tracked
  discrepancyMethod = PaymentMethod.CASH;

  @action
  setDiscrepancyMethod(value) {
    this.discrepancyMethod = value;
  }

  @tracked
  discrepancyAmount = 0;

  @tracked
  discrepancyReason = '';

  @action
  setDiscrepancyReason(value) {
    this.discrepancyReason = value;
  }

  @tracked
  discrepancyDepositSlip = null;

  @computed(
    'transactions.@each.{amount,method}',
    'cancelled.@each.{amount,method}',
  )
  get cashTransactionsTotal() {
    if (isEmpty(this.transactions) && isEmpty(this.cancelled)) {
      return 0;
    }
    // Total up new transactions.
    let total = this.transactions
      .filter((transaction) => transaction.method === PaymentMethod.CASH)
      .reduce((total, transaction) => {
        if (
          transaction.type === TransactionType.REFUND &&
          !isEmpty(transaction.line_item_id)
        ) {
          // Refunds from line items need to be inverted.
          return total - transaction.amount;
        }
        return total + transaction.amount;
      }, 0);
    // Subtract any cancelled transactions.
    return this.cancelled
      .filter((transaction) => transaction.method === PaymentMethod.CASH)
      .reduce((total, transaction) => {
        if (
          transaction.type === TransactionType.REFUND &&
          !isEmpty(transaction.line_item_id)
        ) {
          // Refunds from line items need to be inverted.
          return total + transaction.amount;
        }
        return total - transaction.amount;
      }, total);
  }

  @computed('transactions.@each.method')
  get chequeTransactions() {
    if (isEmpty(this.transactions)) {
      return;
    }
    return this.transactions.filter(
      (transaction) => transaction.method === PaymentMethod.CHEQUE,
    );
  }

  @computed('chequeTransactions.@each.amount')
  get chequeTransactionsTotal() {
    if (isEmpty(this.chequeTransactions)) {
      return 0;
    }
    return this.chequeTransactions.reduce((total, transaction) => {
      if (transaction.isCancelled) {
        let isSameSession =
          transaction.get('tillSession.id') ===
          transaction.get('cancellationTillSession.id');
        let isSameSlip =
          transaction.get('tillSession.depositSlip.id') ===
          transaction.get('cancellationTillSession.depositSlip.id');
        if (
          (this.type === 'tillSession' && isSameSession) ||
          (this.type === 'depositSlip' && isSameSlip)
        ) {
          // This transaction was created and cancelled within the same Till
          // Session or Deposit Slip, so doesn't contribute to the total.
          return total;
        }
      }
      return total + transaction.amount;
    }, 0);
  }

  @computed('transactions.@each.method')
  get moneyOrderTransactions() {
    if (isEmpty(this.transactions)) {
      return;
    }
    return this.transactions.filter(
      (transaction) => transaction.method === PaymentMethod.MONEY_ORDER,
    );
  }

  @computed('moneyOrderTransactions.@each.amount')
  get moneyOrderTransactionsTotal() {
    if (isEmpty(this.moneyOrderTransactions)) {
      return 0;
    }
    return this.moneyOrderTransactions.reduce((total, transaction) => {
      if (
        transaction.isCancelled &&
        transaction.get('tillSession.id') ===
          transaction.get('cancellationTillSession.id')
      ) {
        // This transaction was created and cancelled within the same Till
        // Session, so doesn't contribute to the total amount.
        return total;
      }
      return total + transaction.amount;
    }, 0);
  }

  @computed('showAllMethods', 'transactions.@each.{method,amount}')
  get otherMethods() {
    if (isEmpty(this.transactions) || !this.showAllMethods) {
      return {};
    }
    let excludedMethods = [
      PaymentMethod.CASH,
      PaymentMethod.CHEQUE,
      PaymentMethod.MONEY_ORDER,
      PaymentMethod.INTERNET,
      PaymentMethod.JOURNAL,
    ];
    return this.transactions.reduce((methods, txn) => {
      if (excludedMethods.includes(txn.method)) {
        return methods;
      }
      let label = this.methodLabel(txn.method);
      if (isEmpty(methods[label])) {
        methods[label] = {
          total: 0,
          count: 0,
        };
      }
      if (txn.type === TransactionType.REFUND && !isEmpty(txn.line_item_id)) {
        // Refunds from line items need to be inverted.
        methods[label].total -= txn.amount;
      } else {
        methods[label].total += txn.amount;
      }
      methods[label].count++;
      return methods;
    }, {});
  }

  @computed('showAllMethods', 'cancelled.@each.{method,amount}')
  get otherMethodsCancelled() {
    if (isEmpty(this.cancelled) || !this.showAllMethods) {
      return {};
    }
    let excludedMethods = [
      PaymentMethod.CASH,
      PaymentMethod.CHEQUE,
      PaymentMethod.MONEY_ORDER,
      PaymentMethod.INTERNET,
      PaymentMethod.JOURNAL,
    ];
    return this.cancelled.reduce((methods, txn) => {
      if (excludedMethods.includes(txn.method)) {
        return methods;
      }
      let label = this.methodLabel(txn.method);
      if (isEmpty(methods[label])) {
        methods[label] = {
          total: 0,
          count: 0,
        };
      }
      // Since refunds can't be cancelled, we don't need to worry about them.
      methods[label].total -= txn.amount;
      methods[label].count++;
      return methods;
    }, {});
  }

  @computed(
    'cashTransactionsTotal',
    'chequeTransactionsTotal',
    'moneyOrderTransactionsTotal',
    'entity.discrepanciesTotal',
    'showAllMethods',
    'otherMethods',
    'otherMethodsCancelled',
  )
  get bankingTotal() {
    let total =
      this.cashTransactionsTotal +
      this.chequeTransactionsTotal +
      this.moneyOrderTransactionsTotal -
      this.entity.discrepanciesTotal;

    if (this.showAllMethods) {
      total = Object.values(this.otherMethods).reduce((t, method) => {
        return t + method.total;
      }, total);
      total = Object.values(this.otherMethodsCancelled).reduce((t, method) => {
        return t + method.total;
      }, total);
    }

    return total;
  }

  // methodLabel gets the appropriate label for the specified payment method.
  methodLabel(method) {
    if (method === PaymentMethod.EXTERNAL) {
      // NSW calls this Third Party.
      return 'Third Party';
    }
    return PaymentMethod.getLabel(method);
  }

  @computed(
    'session.person.id',
    'entity.{person.id,isChecked,depositSlip.id}',
    'type',
  )
  get entityReadOnly() {
    if (isEmpty(this.entity)) {
      // No entity?  We shouldn't be here.  Don't allow editing.
      return true;
    }
    if (this.entity.isChecked) {
      // Deposit slip already checked - can no longer be edited.
      return true;
    }
    if (this.type === 'tillSession') {
      if (!isEmpty(this.entity.get('depositSlip.id'))) {
        // This Till Session has been bundled into a Deposit Slip.
        return true;
      }
      if (this.roles.isPrincipalOrDelegate) {
        // Principal/Delegate can close any Till Session.
        return false;
      }
    }
    // Only the person who created the entity can edit it.
    return this.entity.get('person.id') !== this.session.get('person.id');
  }

  @computed(
    'session.person',
    'cashTransactionsTotal',
    'entity.{totalAmount,person,isChecked,cashDiscrepanciesTotal}',
    'depositSlipChecked',
    'entityReadOnly',
    'type',
  )
  get saveDisabled() {
    let total = this.cashTransactionsTotal - this.entity.cashDiscrepanciesTotal;
    if (total.toFixed(2) !== this.get('entity.totalAmount').toFixed(2)) {
      // Cash Reconciliation isn't complete.
      return true;
    }

    if (
      this.type === 'depositSlip' &&
      this.get('session.person.id') !== this.get('entity.person.id')
    ) {
      // Not the original user; require "checked" box to be checked.
      return !this.depositSlipChecked;
    }

    // Otherwise, allow saving (unless read-only).
    return this.entityReadOnly;
  }

  @or(
    'discrepancy.isAcknowledged',
    'discrepancy.depositSlip.isChecked',
    'entityReadOnly',
  )
  discrepancyIsLocked;

  @tracked
  deleteDiscrepancyDialogOpen;

  @tracked
  discrepancyDeleteInProgress;

  @tracked
  saveInProgress;

  @tracked
  discrepancyTillSession;

  @action
  closeDialog() {
    this.onClose();
  }

  @action
  save() {
    this.saveInProgress = true;
    if (this.type === 'tillSession') {
      // Saving a Till Session implies that we're closing it (or it's already
      // closed, in which case this does nothing).
      this.entity.set('isOpen', false);
    }
    this.entity
      .save()
      .then(() => {
        if (this.depositSlipChecked) {
          let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/depositslips/${this.entity.id}/:approve`;
          let data = {
            schoolId: this.session.activeSchool,
          };
          return this.fetch
            .post(url, { data })
            .then(() => this.entity.reload());
        }
      })
      .then(() => {
        this.toaster.success(`${this.entityLabel} saved successfully`);
        this.onClose();
      })
      .catch(() => {
        this.toaster.error(`Error saving ${this.entityLabel}`);
        this.saveInProgress = false;
      });
  }

  @action
  addDiscrepancy() {
    this.resetDiscrepancy();
    this.discrepancyDialogOpen = true;
  }

  @action
  formatAmount(prop) {
    this.set(prop, conformAmount(this.get(prop)));
  }

  @action
  editDiscrepancy(discrepancy) {
    return this.editDiscrepancyTask.perform(discrepancy);
  }

  // Restartable: a click on another row cancels a load that is not done, so
  // the form never holds the records of an older row.
  editDiscrepancyTask = restartableTask(async (discrepancy) => {
    // A belongsTo takes a record, not the async proxy. belongsTo().value()
    // gives null for a relationship that is not loaded, and the save then
    // clears it, so load the records.
    const relationships = await hash({
      depositSlip: discrepancy.depositSlip,
      tillSession: discrepancy.tillSession,
    });
    this.discrepancy = discrepancy;
    this.discrepancyMethod = discrepancy.method;
    this.discrepancyAmount = discrepancy.amount;
    this.discrepancyReason = discrepancy.reason;
    this.discrepancyDepositSlip = relationships.depositSlip;
    this.discrepancyTillSession = relationships.tillSession;
    // Open the dialog when it has the records, so a save cannot clear them.
    this.discrepancyDialogOpen = true;
  });

  @action
  setDiscrepancyAmount(amount) {
    this.discrepancyAmount = conformAmount(amount);
  }

  @action
  saveDiscrepancy() {
    this.discrepancySaving = true;
    let discrepancy = this.discrepancy;
    let isNew = isEmpty(discrepancy);
    if (isNew) {
      discrepancy = this.store.createRecord('finance/discrepancy', {
        schoolId: this.session.school,
        personId: this.session.person,
      });
    }
    discrepancy.setProperties({
      method: this.discrepancyMethod,
      amount: this.discrepancyAmount,
      reason: this.discrepancyReason,
      depositSlip: this.discrepancyDepositSlip,
      tillSession: this.discrepancyTillSession,
    });
    return discrepancy
      .save()
      .then(() => {
        this.discrepancy = null;
        this.discrepancyDialogOpen = false;
        if (isNew) {
          this.discrepancies.update();
        }
      })
      .catch(() => {
        // Unload a new record and revert an edited one, or the failed amount
        // stays in the slip's discrepancies total. The form keeps the input
        // for a retry.
        rollbackRecord(discrepancy);
        this.toaster.error('Error saving discrepancy!');
      })
      .finally(() => {
        this.discrepancySaving = false;
      });
  }

  @action
  closeDiscrepancyDialog() {
    this.discrepancyDialogOpen = false;
  }

  @action
  deleteDiscrepancy() {
    this.deleteDiscrepancyDialogOpen = true;
    this.discrepancyDeleteInProgress = false;
  }

  @action
  doDeleteDiscrepancy() {
    this.discrepancyDeleteInProgress = true;
    this.discrepancy
      .destroyRecord()
      .then(() => {
        this.discrepancy = null;
        this.discrepancyDialogOpen = false;
        this.deleteDiscrepancyDialogOpen = false;
      })
      .catch(() => {
        this.toaster.error('Error deleting Discrepancy.');
      })
      .finally(() => {
        this.discrepancyDeleteInProgress = false;
      });
  }

  @action
  closeDeleteDiscrepancyDialog() {
    this.deleteDiscrepancyDialogOpen = false;
  }

  resetDiscrepancy() {
    let props = {
      discrepancy: null,
      discrepancySaving: false,
      discrepancyMethod: PaymentMethod.CASH,
      discrepancyAmount: 0,
      discrepancyReason: '',
      discrepancyDepositSlip: null,
      discrepancyTillSession: null,
    };

    switch (this.type) {
      case 'depositSlip':
        props.discrepancyDepositSlip = this.entity;
        break;
      case 'tillSession':
        props.discrepancyTillSession = this.entity;
        break;
    }

    this.setProperties(props);
  }
}
